import type { ReactNode } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconEyeOutline18 as Eye,
	IconEyeSlashOutline18 as EyeOff,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { NucleoFillIcon, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { ArticleVisibility } from "./article-visibility"
import { RoleCheck } from "@/components/shared/role-check"
import { useRole } from "@/lib/useRole"
import { useZero } from "@/lib/zero"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import {
	displayUrl,
	getArticleDisplayTitle,
	isLinkArticle,
} from "./article-kind"
import { ArticleStatePill } from "./article-state-pill"
import { getArticlePillState } from "./article-visibility"

export const HOME_TILE_CLASS =
	"group relative flex cursor-default flex-col flex-wrap overflow-hidden rounded-lg bg-black/[0.02] px-3 py-5 ring-1 ring-black/15 hover:bg-gray-100 lg:px-[1.2rem] dark:bg-gray-850 dark:shadow-black dark:ring-white/10 dark:hover:bg-gray-800"

export interface HelpCenterHomeTileProps {
	article: {
		id: string
		urlName: string
		title: string
		summary: string | null
		icon: string
		showOnHomePage: boolean
		published: boolean
		visibility: ArticleVisibility
		helpCenterGroupId: string | null
		kind?: "DOC" | "LINK" | null
		externalUrl?: string | null
	}
	titleComponent?: ReactNode
	summaryComponent?: ReactNode
	/** Replaces the home toggle, for a tile that already sits in a section. */
	action?: ReactNode
}

export function HelpCenterHomeTile({
	article,
	titleComponent,
	summaryComponent,
	action,
}: HelpCenterHomeTileProps) {
	const zero = useZero()
	const { role } = useRole()
	const navigate = useNavigate()

	const { openGroup } = useDocNavStore()
	const pillState = getArticlePillState(article)
	const isLink = isLinkArticle(article)

	const updateShowOnHomePage = (value: boolean) => {
		void zero.mutate(
			mutators.helpCenterArticles.update({
				id: article.id,
				showOnHomePage: value,
			}),
		)
	}

	return (
		<div
			role="button"
			tabIndex={0}
			aria-pressed={article.showOnHomePage}
			onClick={() => {
				void navigate({ to: "/edit-docs/$id", params: { id: article.id } })
				if (article.helpCenterGroupId) {
					openGroup(article.helpCenterGroupId)
				}
			}}
			onKeyDown={(e) => {
				if (role === "VIEWER") return
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					updateShowOnHomePage(!article.showOnHomePage)
				}
			}}
			key={article.urlName}
			className={cn(
				HOME_TILE_CLASS,
				// A link is never published or shown on the docs home, so the dimming
				// those two flags drive would leave every link tile permanently faded.
				!isLink && !article.showOnHomePage && "opacity-50",
				!isLink && !article.published && "opacity-50",
			)}
		>
			<div
				className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[var(--portal-accent-color)] to-transparent opacity-[0.00]"
				style={{
					background:
						"linear-gradient(135deg, var(--portal-accent-color) 0%, transparent 100%)",
				}}
			/>
			<div className="relative">
				{/* Top row: Icon left, Eye/EyeOff or state pill right */}
				<div className="mb-2 flex items-center justify-between">
					<div className="flex h-6 w-6 items-center justify-center overflow-hidden">
						<NucleoFillIcon
							icon={article.icon}
							className="size-[18px] min-h-[18px] min-w-[18px] text-primary"
						/>
					</div>
					<div className="-mr-1 -mt-1">
						{action ? (
							action
						) : isLink ? null : pillState !== "none" ? (
							<ArticleStatePill
								article={article}
								className="my-auto"
								withTooltip
							/>
						) : (
							<RoleCheck not={["VIEWER"]}>
								<Tooltip
									label={
										article.showOnHomePage ? "Hide from home" : "Show on home"
									}
								>
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation()
											updateShowOnHomePage(!article.showOnHomePage)
										}}
										aria-label={
											article.showOnHomePage ? "Hide from home" : "Show on home"
										}
										className={cn(
											"flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-opacity hover:text-primary focus-visible:opacity-100 group-hover:opacity-100 dark:text-gray-400 dark:hover:text-primary",
											article.showOnHomePage && "opacity-0",
										)}
									>
										{article.showOnHomePage ? (
											<Eye className="size-4" />
										) : (
											<EyeOff className="size-4" />
										)}
									</button>
								</Tooltip>
							</RoleCheck>
						)}
					</div>
				</div>

				<h3 className="mt-4 line-clamp-1 min-w-0 text-ellipsis text-[0.9rem] font-medium tracking-tight text-primary sm:mt-10">
					{titleComponent ?? getArticleDisplayTitle(article)}
				</h3>
				{isLink
					? article.externalUrl && (
							<p className="mt-1 line-clamp-1 min-w-0 overflow-hidden text-ellipsis text-[0.8rem] tracking-tight text-gray-500 opacity-90 dark:text-gray-400">
								{displayUrl(article.externalUrl)}
							</p>
						)
					: (summaryComponent || article.summary) && (
							<p className="mt-1 line-clamp-2 min-w-0 overflow-hidden text-ellipsis text-[0.8rem] tracking-tight text-gray-500 opacity-90 dark:text-gray-400">
								{summaryComponent || article.summary}
							</p>
						)}
			</div>
		</div>
	)
}
